import { Box, Button, HStack, Stack, Text } from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FiDownload, FiFile } from "react-icons/fi";
import { bffApi, type PortalMarketDialogueDto } from "../../../autogen/bff-api";
import { useApiError } from "../../../common/errors/useApiError";
import { momentLocaleFor } from "../../../common/formatting/i18nFormatters";
import { useAppDispatch } from "../../../common/redux/hooks";
import { formatShortDate } from "../../sourcing-events/portal/utils/displayFormatters";
import { SectionCard } from "../../sourcing-events/portal/view-single/SectionCard";

export const PortalMarketDialogueDocuments = ({
  dialogue,
  asSupplier = false,
}: {
  dialogue: PortalMarketDialogueDto;
  asSupplier?: boolean;
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const displayer = useApiError();
  const [pendingId, setPendingId] = useState<string | null>(null);
  const locale = momentLocaleFor(i18n.language);

  if (dialogue.documents.length === 0) return null;

  const download = async (documentId: string) => {
    setPendingId(documentId);
    try {
      const link = asSupplier
        ? bffApi.endpoints.getSupplierMarketDialogueDocumentDownloadLink
        : bffApi.endpoints.getPortalMarketDialogueDocumentDownloadLink;
      const result = await dispatch(link.initiate({ marketDialogueId: dialogue.id, documentId })).unwrap();
      window.open(result.url, "_blank", "noopener,noreferrer");
    } catch (error) {
      displayer.trigger(error);
    } finally {
      setPendingId(null);
    }
  };

  return (
    <SectionCard icon={FiDownload} title={t("Documents")} count={dialogue.documents.length}>
      <Stack spacing="4">
        {dialogue.documents.map((document) => (
          <HStack
            key={document.id}
            borderWidth="1px"
            borderColor="smBorder"
            rounded="md"
            px="4"
            py="3"
            bg="smBackground"
            justify="space-between"
            spacing="3"
          >
            <HStack spacing="3" flex="1" minW="0">
              <Box color="smMuted" flexShrink="0">
                <FiFile size={18} />
              </Box>
              <Stack spacing="0" minW="0">
                <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>
                  {document.name}
                </Text>
                <Text fontSize="xs" color="smMuted">
                  {formatShortDate(document.uploadedAt, locale)}
                </Text>
              </Stack>
            </HStack>
            <Button
              size="sm"
              variant="outline"
              colorScheme="gray"
              leftIcon={<FiDownload />}
              onClick={() => download(document.id)}
              isLoading={pendingId === document.id}
              flexShrink="0"
            >
              {t("Download")}
            </Button>
          </HStack>
        ))}
      </Stack>
    </SectionCard>
  );
};
